import React, { useRef, useState } from "react";
import { UploadCloud, X, GripVertical, Loader2, ImageOff } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const MAX_FILE_MB = 10;

export default function ImageUpload({ images = [], onChange, single = false }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState([]);
  const dragIndex = useRef(null);

  const uploadFile = async (file) => {
    const id = `${file.name}-${Date.now()}-${Math.random()}`;
    if (!file.type.startsWith("image/")) { toast.error(`${file.name} is not an image`); return; }
    if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`${file.name} is larger than ${MAX_FILE_MB}MB`); return; }

    setUploads((u) => [...u, { id, name: file.name, progress: 0, error: null }]);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", form, {
        onUploadProgress: (evt) => {
          const progress = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress } : x)));
        },
      });
      // In single mode a new upload replaces the current image instead of
      // adding to it -- the old Cloudinary asset is left as-is (not
      // deleted), only the stored URL is swapped.
      onChange(single ? [data.url] : [...(images || []), data.url]);
      setUploads((u) => u.filter((x) => x.id !== id));
    } catch (err) {
      const message = apiError(err.response?.data?.detail);
      setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: message } : x)));
      toast.error(`Failed to upload ${file.name}`);
    }
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    (single ? files.slice(0, 1) : files).forEach(uploadFile);
  };

  const removeImage = (idx) => {
    const next = [...images];
    next.splice(idx, 1);
    onChange(next);
  };

  const dismissUpload = (id) => setUploads((u) => u.filter((x) => x.id !== id));

  const onDropReorder = (idx) => {
    const from = dragIndex.current;
    if (from === null || from === idx) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    onChange(next);
    dragIndex.current = null;
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-8 text-center transition ${dragOver ? "border-gold bg-gold/5" : "border-border bg-cream/60 hover:border-gold/60"}`}
        data-testid="image-upload-dropzone"
      >
        <UploadCloud size={22} className="text-navy/40" />
        <p className="text-sm font-medium text-navy">
          {single
            ? (images && images.length > 0 ? "Drag & drop a replacement image, or click to browse" : "Drag & drop an image, or click to browse")
            : "Drag & drop images, or click to browse"}
        </p>
        <p className="text-xs text-muted-foreground">JPG, PNG, WEBP or GIF, up to {MAX_FILE_MB}MB each</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={!single}
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {uploads.length > 0 && (
        <div className="mt-3 space-y-2">
          {uploads.map((u) => (
            <div key={u.id} className="rounded-lg border border-border bg-white p-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-navy">{u.name}</span>
                {u.error ? (
                  <button type="button" onClick={() => dismissUpload(u.id)} className="text-red-500">{u.error} · Dismiss</button>
                ) : (
                  <Loader2 size={14} className="animate-spin text-gold-600" />
                )}
              </div>
              {!u.error && (
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-cream">
                  <div className="h-full bg-gold transition-all" style={{ width: `${u.progress}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {images && images.length > 0 && (
        <div className={`mt-4 grid gap-3 ${single ? "grid-cols-1 max-w-[160px]" : "grid-cols-3 sm:grid-cols-4"}`}>
          {images.map((src, idx) => (
            <div
              key={src + idx}
              draggable={!single}
              onDragStart={() => { dragIndex.current = idx; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropReorder(idx)}
              className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-cream"
              data-testid={single ? "category-image" : `product-image-${idx}`}
            >
              <img src={src} alt="" className="h-full w-full object-cover" />
              {idx === 0 && !single && (
                <span className="absolute left-1.5 top-1.5 rounded-full bg-navy/80 px-2 py-0.5 text-[10px] font-semibold text-cream">Primary</span>
              )}
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute right-1.5 top-1.5 rounded-full bg-navy/80 p-1 text-cream opacity-0 transition group-hover:opacity-100"
                aria-label="Remove image"
              >
                <X size={12} />
              </button>
              {!single && (
                <div className="absolute bottom-1.5 right-1.5 cursor-grab rounded-full bg-navy/70 p-1 text-cream opacity-0 transition group-hover:opacity-100" title="Drag to reorder">
                  <GripVertical size={12} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(!images || images.length === 0) && uploads.length === 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <ImageOff size={14} /> {single ? "No image yet." : "No images yet — the first image becomes the primary product photo."}
        </div>
      )}
    </div>
  );
}
